import { SuperChoiceType } from "../generated/graphql";

/**
 * Is the signed-in employee's OWN profile finished?
 *
 * One formula, two consumers (2026-08-21): the home-page nudge (`EmployeeHomePanel`) and the
 * module-tile landing decision (`AdminModuleLinks`) — because a self-service employee should
 * land on My Details while there are gaps and on My Shifts once there aren't.
 *
 * It was already duplicated in `MyDetails.tsx` and the backend's `getMissingFields`, and a
 * third FE copy was about to appear. Extracted rather than copied again; the two remaining
 * copies are noted so they can be kept in step.
 *
 * Deliberately the same FIVE profile sections the home nudge has always used. MyDetails
 * additionally counts a 6th "sign forms" item, which needs another query — keeping this one
 * query-light is why the split exists.
 */

export interface ProfileSectionState {
  label: string;
  done: boolean;
}

/** `profile` is the `myEmployeeProfile` shape; loosely typed so both callers can pass theirs. */
export function employeeProfileSections(profile: any): ProfileSectionState[] {
  const e = profile ?? {};
  return [
    { label: "Profile photo", done: !!e.profilePhoto },
    {
      label: "Personal details",
      done: !!(
        e.client?.fName &&
        e.client?.lName &&
        e.client?.email &&
        e.address?.street &&
        e.address?.city &&
        e.address?.state &&
        e.address?.postcode
      ),
    },
    { label: "Emergency contact", done: !!(e.emergencyContact?.name && e.emergencyContact?.phoneNumber) },
    { label: "Bank details", done: !!(e.bankAccountName && e.bankBSB && e.bankAccountNumber) },
    {
      label: "Tax & work rights",
      // EMPLOYER_DEFAULT needs no fund name — matches MyDetails + the backend's getMissingFields.
      done: !!(
        e.taxFileNumber &&
        e.taxResidencyStatus &&
        e.claimsTaxFreeThreshold != null &&
        e.hasStudyTrainingLoan != null &&
        e.superChoiceType &&
        (e.superChoiceType === SuperChoiceType.EmployerDefault || e.superannuationFund)
      ),
    },
  ];
}

/** True when at least one section is still outstanding. A missing profile reads as complete,
 *  so a viewer with no employee record is never nagged to fill one in. */
export function isOwnProfileIncomplete(profile: any): boolean {
  if (!profile) return false;
  return employeeProfileSections(profile).some((s) => !s.done);
}
